import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import {SketchPointKind} from '../../mesh/sketch/ISketchObject';
import {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {QuickSnapMode} from './QuickSnapMode';
import {GeometryInteractionState} from '../interactionState/GeometryInteractionState';

/**
 * 端点和草图点捕捉策略。
 * 说明：
 * 1. 普通模式：扫描端点和用户独立创建的草图点，并按策略阈值吸附最近点；
 * 2. 快速点捕捉模式（QuickSnapMode.Point）：
 *    - 全局搜索端点、草图点、圆/圆弧/椭圆圆心点、椭圆象限点（无距离限制）；
 *    - 高亮最近候选点（按 pointKind 使用对应 Hover 材质），不高亮边线；
 *    - 权重 200 确保必定胜出。
 */
export class EndpointOriginPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.EndpointOrigin;

    /**
     * 端点/草图点普通捕捉像素阈值。
     * 说明：覆盖父类默认配置，保证端点策略可以独立调整捕捉吸附范围。
     */
    protected override readonly snapPixelThreshold: number = 10;

    /** 端点/草图点普通捕捉权重。 */
    private static readonly WEIGHT = 100;

    /** 快速捕捉模式权重，确保必定胜出所有普通策略。 */
    private static readonly QUICK_SNAP_WEIGHT = 200;

    /** 快速捕捉模式下当前高亮的点对象。 */
    private quickSnapHighlightedPoint: SketchPointObject | null = null;

    /** 快速捕捉模式下当前高亮点的原始可见性。 */
    private quickSnapOriginalVisibility: boolean | null = null;

    /**
     * 草图会话原点投影与正式点重合判定容差平方。
     * 说明：
     * 1. 仅用于“临时投影点 vs 正式草图点”优先级收口；
     * 2. 容差保持在较小量级，避免把邻近但非重合的点误判为同一点。
     */
    private static readonly COINCIDENT_FORMAL_POINT_DISTANCE_SQUARED = 1e-8;

    /**
     * 执行端点和草图点捕捉求解。
     * 说明：普通模式不依赖 hoveredObject，直接根据鼠标到端点/草图点的屏幕距离触发捕捉。
     * @param request 点捕捉请求参数。
     * @returns 最近点捕捉结果；阈值内无候选点时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        
        if (request.quickSnapMode === QuickSnapMode.Point) {
            return this.resolveQuickSnap(request);
        }
        

        const transientPointResult = this.resolveTransientPointSnap(request);
        if (transientPointResult) {
            const preferredTransientResult = this.resolvePreferredFormalPointResult(transientPointResult);
            
            this.syncSnapHoverState(preferredTransientResult.targetObject);
            
            return preferredTransientResult;
        }

        
        const formalPointResult = this.resolveFormalPointSnap(request);
        

        
        this.syncSnapHoverState(formalPointResult?.targetObject ?? null);
        
        return formalPointResult;
    }

    /**
     * 解析当前交互流程临时白名单点的普通捕捉。
     * 说明：
     * 1. 仅对白名单中的少量预览点执行屏幕距离判定；
     * 2. 当前用于草图会话投影点和“样条绘制时允许捕捉自身首点”；
     * 3. 命中后直接返回端点捕捉结果，不再依赖 hoveredObject。
     * @param request 点捕捉请求参数。
     * @returns 临时点捕捉结果；未命中时返回 null。
     */
    private resolveTransientPointSnap(request: PointSnapRequest): PointSnapResult | null {
        const transientPointObjects = request.transientPointObjects ?? [];
        let bestPoint: SketchPointObject | null = null;
        let bestDistance = this.snapPixelThreshold;

        for (const point of transientPointObjects) {
            // 模型边真实中点由 MidpointPointSnapStrategy 独立处理，避免返回错误的端点图标和门禁语义。
            if (
                point.isSketchSessionModelEdgeMidpoint
                || point.isSketchSessionModelEdgeCenter
                || point.isSketchSessionModelEdgeQuadrant
                || !point.visible
                || this.isSnapTargetExcluded(request, point)
            ) {
                continue;
            }
            const screenPosition = this.projectWorldToScreen(point.getPosition());
            if (!screenPosition) {
                continue;
            }
            const distance = screenPosition.distanceTo(request.screenPosition);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestPoint = point;
            }
        }

        if (!bestPoint) {
            return null;
        }

        const worldPosition = bestPoint.getPosition();
        return {
            strategyType: this.strategyType,
            position: worldPosition.clone(),
            indicatorPosition: worldPosition.clone(),
            targetObject: bestPoint,
            weight: EndpointOriginPointSnapStrategy.WEIGHT,
        };
    }

    
    /**
     * 解析正式端点和用户草图点的普通距离捕捉。
     * 说明：
     * 1. 普通捕捉必须根据鼠标到点投影的屏幕距离触发，而不能依赖选择层 hoveredObject；
     * 2. 候选来源限定为活跃草图组维护的端点集合和用户草图点集合，避免误吸附中点、圆心、象限点；
     * 3. 在阈值内取屏幕距离最近点，保持端点/草图点策略自身的吸附语义闭环。
     * @param request 点捕捉请求参数。
     * @returns 最近正式点捕捉结果；阈值内无候选点时返回 null。
     */
    private resolveFormalPointSnap(request: PointSnapRequest): PointSnapResult | null {
        let bestPoint: SketchPointObject | null = null;
        let bestDistance = this.snapPixelThreshold;

        /**
         * 扫描一组正式点候选，并更新当前最近命中点。
         * @param collection 正式点集合。
         */
        const scanCollection = (collection: ReadonlySet<SketchPointObject> | null): void => {
            if (collection === null) {
                return;
            }

            for (const point of collection) {
                if (this.isSnapTargetExcluded(request, point)) {
                    continue;
                }

                if (!this.isFormalEndpointOrSketchPoint(point)) {
                    continue;
                }

                // 正式点必须投影到屏幕后再按像素距离比较，避免世界单位随缩放改变交互手感。
                const screenPosition = this.projectWorldToScreen(point.getPosition());
                if (screenPosition === null) {
                    continue;
                }

                const distance = screenPosition.distanceTo(request.screenPosition);
                if (distance < bestDistance) {
                    bestDistance = distance;
                    bestPoint = point;
                }
            }
        };

        scanCollection(this.getActiveEndPoints());
        scanCollection(this.getActiveSketchKindPoints());

        if (bestPoint === null) {
            return null;
        }

        const preferredPoint = this.resolvePreferredFormalPointTarget(bestPoint);
        const preferredPosition = preferredPoint.getPosition();
        return {
            strategyType: this.strategyType,
            position: preferredPosition.clone(),
            indicatorPosition: preferredPosition.clone(),
            targetObject: preferredPoint,
            weight: EndpointOriginPointSnapStrategy.WEIGHT,
        };
    }

    /**
     * 判断点对象是否允许参与普通端点/草图点距离捕捉。
     * 说明：
     * 1. 绘制态预览点由 transientPointObjects 白名单单独接入，这里必须排除；
     * 2. 普通正式点只允许 ControlPoint 与 SketchPoint，避免圆心、中点、象限点抢占端点语义。
     * @param point 待判断点对象。
     * @returns 可参与普通距离捕捉返回 true，否则返回 false。
     */
    private isFormalEndpointOrSketchPoint(point: SketchPointObject): boolean {
        if (point.isDrawingPreviewPoint) {
            return false;
        }

        const kind = point.pointKind;
        return kind === SketchPointKind.ControlPoint || kind === SketchPointKind.SketchPoint;
    }
    

    /**
     * 将临时原点命中的捕捉结果重写为正式重合点命中。
     * 说明：
     * 1. 当草图会话原点投影与正式草图点重合时，后续捕捉必须优先落到正式点；
     * 2. 这样新创建实体的控制点/草图点会成为后续捕捉真相，而临时原点只保留参考作用；
     * 3. 非临时原点目标保持原结果不变。
     * @param result 原始捕捉结果。
     * @returns 已按优先级收口后的捕捉结果。
     */
    private resolvePreferredFormalPointResult(result: PointSnapResult): PointSnapResult {
        const targetObject = result.targetObject;
        if (!(targetObject instanceof SketchPointObject)) {
            return result;
        }

        const preferredTarget = this.resolvePreferredFormalPointTarget(targetObject);
        if (preferredTarget === targetObject) {
            return result;
        }

        const preferredPosition = preferredTarget.getPosition();
        return {
            ...result,
            position: preferredPosition.clone(),
            indicatorPosition: preferredPosition.clone(),
            targetObject: preferredTarget,
        };
    }

    /**
     * 解析当前点是否需要让位于重合的正式草图点。
     * 说明：
     * 1. 仅草图会话临时参考点会触发该分支；
     * 2. 若活跃草图组中已存在与其重合的正式点，优先返回正式点；
     * 3. 控制点优先级高于独立草图点，保持后续复用语义更稳定。
     * @param target 当前命中的点对象。
     * @returns 最终应作为捕捉目标的点对象。
     */
    private resolvePreferredFormalPointTarget(target: SketchPointObject): SketchPointObject {
        
        // 特征面边角点/边中点与会话原点一样只作为参考；重合正式点应优先承接复用语义。
        if (!target.isDrawingSketchOrigin && !target.isSketchSessionReferencePoint) {
            return target;
        }
        

        return this.resolveCoincidentFormalPoint(target) ?? target;
    }

    /**
     * 查找与草图会话临时参考点重合的正式草图点。
     * @param originPoint 草图会话临时参考点。
     * @returns 重合正式点；不存在时返回 null。
     */
    private resolveCoincidentFormalPoint(originPoint: SketchPointObject): SketchPointObject | null {
        const activeSketchPoints = this.getActiveSketchPointObjects();
        if (!activeSketchPoints) {
            return null;
        }

        const originPosition = originPoint.getPosition();
        let fallbackSketchPoint: SketchPointObject | null = null;

        for (const point of activeSketchPoints) {
            
            // 临时参考点不参与正式点复用候选，避免边界投影点被误当作可共享控制点。
            if (point === originPoint || point.isDrawingPreviewPoint || point.isDrawingSketchOrigin || point.isSketchSessionReferencePoint) {
                continue;
            }
            

            const kind = point.pointKind;
            if (kind !== SketchPointKind.ControlPoint && kind !== SketchPointKind.SketchPoint) {
                continue;
            }

            const distanceSquared = point.getPosition().distanceToSquared(originPosition);
            if (distanceSquared > EndpointOriginPointSnapStrategy.COINCIDENT_FORMAL_POINT_DISTANCE_SQUARED) {
                continue;
            }

            if (kind === SketchPointKind.ControlPoint) {
                return point;
            }
            fallbackSketchPoint = point;
        }

        return fallbackSketchPoint;
    }

    /**
     * 清除快速捕捉高亮预览。
     */
    public override clearPreview(): void {
        this.clearQuickSnapHighlight();
    }

    /**
     * 快速点捕捉求解：全局收集所有候选点，找最近者高亮并捕捉。
     * 候选点来源：端点、用户草图点、圆心点、椭圆象限点。
     * @param request 点捕捉请求参数。
     * @returns 最近候选点的捕捉结果；无可用点时返回 null。
     */
    private resolveQuickSnap(request: PointSnapRequest): PointSnapResult | null {
        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            this.clearQuickSnapHighlight();
            return null;
        }

        let bestPoint: SketchPointObject | null = null;
        let bestDist = Infinity;

        /**
         * 扫描点集合，无距离限制地找全局最近候选。
         * @param collection 点集合。
         * @param filter 可选过滤函数。
         */
        const scanAll = (
            collection: ReadonlySet<SketchPointObject> | null,
            filter?: (p: SketchPointObject) => boolean,
        ): void => {
            if (!collection) return;
            for (const point of collection) {
                if (point.isDrawingPreviewPoint) continue;
                if (this.isSnapTargetExcluded(request, point)) continue;
                if (filter && !filter(point)) continue;
                const screenPos = this.projectWorldToScreen(point.getPosition());
                if (!screenPos) continue;
                const dist = screenPos.distanceTo(request.screenPosition);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestPoint = point;
                }
            }
        };

        scanAll(group.getEndPoints());
        scanAll(group.getSketchKindPoints());
        scanAll(group.getCenterPoints());
        // 仅椭圆的象限点（isActiveForSnap=true 且关联边为椭圆）。
        scanAll(group.getQuadrantPoints(), (p) => {
            if (!p.isActiveForSnap) return false;
            return p.getConnectedEdgesSnapshot().some(e => isSketchEllipseObject(e));
        });

        if (bestPoint === null) {
            this.clearQuickSnapHighlight();
            return null;
        }

        // 快速模式也复用统一的点移入材质同步，确保切换分支行为一致。
        this.syncSnapHoverState(bestPoint);

        const worldPos = bestPoint.getPosition();
        return {
            strategyType: this.strategyType,
            position: worldPos.clone(),
            targetObject: bestPoint,
            weight: EndpointOriginPointSnapStrategy.QUICK_SNAP_WEIGHT,
        };
    }

    /**
     * 通过交互状态管理器给快速捕捉目标点施加 Hover 高亮。
     * @param point 目标点对象。
     */
    private applyQuickSnapHoverState(point: SketchPointObject): void {
        // 部分圆心点、象限点默认隐藏，快速点捕捉时需要临时显示。
        point.visible = true;
        this.viewer?.interactionStateManager?.setSketchObjectState(
            point,
            GeometryInteractionState.Hover,
        );
    }

    /**
     * 清除当前高亮点，恢复到交互状态管理器统一控制的默认视觉结果。
     */
    private clearQuickSnapHighlight(): void {
        if (this.quickSnapHighlightedPoint !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.quickSnapHighlightedPoint);
            const isTopologyVisibilityManaged = this.quickSnapHighlightedPoint.isEndPoint
                || this.quickSnapHighlightedPoint.isMidPoint
                || this.quickSnapHighlightedPoint.isQuadrantPoint;
            this.quickSnapHighlightedPoint.refreshDefaultVisibilityByTopologyRole();
            if (this.quickSnapOriginalVisibility !== null && !isTopologyVisibilityManaged) {
                this.quickSnapHighlightedPoint.visible = this.quickSnapOriginalVisibility;
            }
        }
        this.quickSnapHighlightedPoint = null;
        this.quickSnapOriginalVisibility = null;
    }

    /**
     * 同步当前吸附目标点的移入材质状态。
     * 说明：
     * 1. 命中点变化时先恢复旧点，再把新点写入 Hover；
     * 2. 命中为空时只执行清理，避免残留高亮；
     * 3. 普通捕捉与快速捕捉统一复用该逻辑，保证视觉行为一致。
     * @param targetObject 当前吸附目标对象。
     */
    private syncSnapHoverState(targetObject: PointSnapResult['targetObject'] | null): void {
        const point = targetObject instanceof SketchPointObject ? targetObject : null;
        if (point === null) {
            this.clearQuickSnapHighlight();
            return;
        }

        // 目标点变化时先恢复旧点，避免旧高亮残留在其他点上。
        if (this.quickSnapHighlightedPoint !== point) {
            this.clearQuickSnapHighlight();
            // 仅在切换到新目标点时记录原始可见性，避免连续帧覆盖丢失“原本隐藏”的状态。
            this.quickSnapOriginalVisibility = point.visible;
        }

        // 无论目标点是否变化，都重新声明当前点处于 Hover 高亮态。
        // 这样即使刷新流程曾重建默认材质，外部状态也会被再次写回，直到显式清除。
        this.applyQuickSnapHoverState(point);
        this.quickSnapHighlightedPoint = point;
    }
}
